import dayjs from "dayjs";
import React, { HTMLAttributes, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATHS } from "../../../../routes/paths/route-paths";
import useCarparkStore from "../../../../store/carpark";
import useGeneralStore from "../../../../store/general";
import useInfoStateStore from "../../../../store/infoState";
import BaseTitle from "../../../atom/baseTitle";
import BaseBreadCrumb from "../../../atom/breadcrumb";
import BookingDetailBlock from "../../bookingDetailBlock";
import SubscriptionPaymentMethodSelectionBlock, {
  type IPaymentMethodsInfo,
} from "../../subscriptionPaymentMethodSelectionBlock";

import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { createCustomSubscription } from "../../../../apis/subscription";
import { customShowToast } from "../../../../helper/toast";

dayjs.extend(utc);
dayjs.extend(timezone);
const HK_TZ = "Asia/Hong_Kong";

interface IConfirmForm {
  goStep: (step: number) => void;
  token?: string;
}

type TConfirmFormProps = IConfirmForm & HTMLAttributes<HTMLDivElement>;

const ConfirmForm: React.FC<TConfirmFormProps> = (props) => {
  const { goStep, token, ...otherProps } = props;

  const navigate = useNavigate();

  const [tncChecked, setTncChecked] = useState(false);
  const [readyToRedirect, setReadyToRedirect] = useState("");
  const [retryData, setRetryData] = useState<
    { monthly_order_id: number; payment_id: number } | undefined
  >(undefined);
  const [reInitPaymentMethod, setReInitPaymentMethod] = useState(false);

  const {
    selectedCarparkID,
    selectedMonthlyItem,
    selectedStartDate,
    applyFormData,
    extraCarPlateDetails,
    clearInfoState,
  } = useInfoStateStore((state) => state);

  console.log(`selectedStartDate in final form`);
  console.log(selectedStartDate);

  const carparkInfoList = useCarparkStore.getState().carparkInfoList;
  const selectedCarparkInfo = carparkInfoList.find(
    (carpark) => carpark.id.toString() === selectedCarparkID
  );

  const setGlobalLoadingState = useGeneralStore((state) => state.setIsLoading);

  // for credit card payment
  const creditCardCreateSubscription = async (
    paymentInfo: IPaymentMethodsInfo
  ) => {
    try {
      setGlobalLoadingState(true);
      setReInitPaymentMethod(false);

      if (applyFormData && selectedStartDate && selectedMonthlyItem) {
        const formattedExtraCarPlateDetails: {
          sub_car_plate: string;
          octopus_number?: string;
        }[] = extraCarPlateDetails.map((item) => {
          return {
            sub_car_plate: item.carplate,
            octopus_number: item.octopusNumber
              ? `${item.octopusNumber}(${item.octopusLastDigit})`
              : undefined,
          };
        });

        const finalAmount =
          (selectedMonthlyItem?.price || 0) +
          (selectedMonthlyItem?.extra_car_plate_price *
            formattedExtraCarPlateDetails.length || 0);

        const lastSubmitStartDate = dayjs
          .tz(selectedStartDate, HK_TZ)
          .startOf("day")
          .toDate();

        const createResult = await createCustomSubscription({
          name: applyFormData?.name,
          email: applyFormData.email,
          tel: applyFormData.phone,
          sub_start_date: lastSubmitStartDate.toISOString(),
          lpn: applyFormData?.mainCarPlate,
          monthlyOrderItemId: selectedMonthlyItem?.id,
          octopus_number:
            applyFormData.octopusNumber && applyFormData?.octopusLastDigit
              ? `${applyFormData.octopusNumber}(${applyFormData?.octopusLastDigit})`
              : undefined,
          wl_token: token || undefined,
          extra_car_plates: formattedExtraCarPlateDetails,
          user_id: applyFormData.userID!,
          amount: finalAmount,
          ...(paymentInfo.adyenCreditCardInfo || {}),
          payment_method_token: paymentInfo.paymentMethodToken,
          monthlyOrderId: retryData?.monthly_order_id || undefined,
          paymentId: retryData?.payment_id || undefined,
        });

        // error case
        if (!createResult) {
          customShowToast(`發生未知錯誤`);
          setGlobalLoadingState(false);
        } else {
          // normal case
          if (createResult.id) {
            setGlobalLoadingState(false);
            clearInfoState();
            setReadyToRedirect(createResult.id.toString());
          } else {
            // special case
            if (createResult === "duplicated") {
              console.log("hi");
              customShowToast(
                `此停車場已存在相同車牌的訂閱，請登入查看相關訂閱。`
              );
            } else if (createResult === "deleted") {
              customShowToast(`找不到該訂閱項目`);
            } else if (createResult === "fulled") {
              customShowToast(`抱歉，當前車場已滿。`);
            } else if (createResult.status === "retry") {
              // bind card action fail
              customShowToast("信用卡綁定失敗，請重新嘗試", "error");
              setRetryData({
                monthly_order_id: createResult.monthly_order_id,
                payment_id: createResult.payment_id,
              });
              setReInitPaymentMethod(true);
              //
            } else if (createResult.new_amount) {
              customShowToast("價錢已更改，請重新確認", "error", () =>
                window.location.reload()
              );
            }
            setGlobalLoadingState(false);
          }
        }
      }
    } catch (error) {
      setGlobalLoadingState(false);
    } finally {
      setGlobalLoadingState(false);
    }
  };

  // for non credit card payment
  const nonCreditCardCreateSubscription = async (
    selectedPaymentMethod: string
  ) => {
    try {
      setGlobalLoadingState(true);
      setReInitPaymentMethod(false);

      if (applyFormData && selectedStartDate && selectedMonthlyItem) {
        const formattedExtraCarPlateDetails: {
          sub_car_plate: string;
          octopus_number?: string;
        }[] = extraCarPlateDetails.map((item) => {
          return {
            sub_car_plate: item.carplate,
            octopus_number: item.octopusNumber
              ? `${item.octopusNumber}(${item.octopusLastDigit})`
              : undefined,
          };
        });

        const finalAmount =
          (selectedMonthlyItem?.price || 0) +
          (selectedMonthlyItem?.extra_car_plate_price *
            formattedExtraCarPlateDetails.length || 0);

        // create a Date that represents HK midnight for the selectedStartDate
        const lastSubmitStartDate = dayjs
          .tz(selectedStartDate, HK_TZ)
          .startOf("day")
          .toDate();

        console.log("to utc string:", lastSubmitStartDate.toISOString());

        const createResult = await createCustomSubscription({
          name: applyFormData?.name,
          email: applyFormData.email,
          tel: applyFormData.phone,
          sub_start_date: lastSubmitStartDate.toISOString(),
          lpn: applyFormData?.mainCarPlate,
          monthlyOrderItemId: selectedMonthlyItem?.id,
          octopus_number:
            applyFormData.octopusNumber && applyFormData?.octopusLastDigit
              ? `${applyFormData.octopusNumber}(${applyFormData?.octopusLastDigit})`
              : undefined,
          wl_token: token || undefined,
          extra_car_plates: formattedExtraCarPlateDetails,
          user_id: applyFormData.userID!,
          amount: finalAmount,
          payment_method_token: selectedPaymentMethod,
          monthlyOrderId: retryData?.monthly_order_id || undefined,
          paymentId: retryData?.payment_id || undefined,
        });

        // error case
        if (!createResult) {
          customShowToast(`發生未知錯誤`);
          setGlobalLoadingState(false);
        } else {
          // normal case
          if (createResult.id) {
            setGlobalLoadingState(false);
            clearInfoState();
            setReadyToRedirect(createResult.id.toString());
          } else {
            // special case
            if (createResult === "duplicated") {
              customShowToast(
                `此停車場已存在相同車牌的訂閱，請登入查看相關訂閱。`
              );
              setReInitPaymentMethod(true);
            } else if (createResult === "deleted") {
              customShowToast(`找不到該訂閱項目`);
              setReInitPaymentMethod(true);
            } else if (createResult === "fulled") {
              customShowToast(`抱歉，當前車場已滿。`);
              setReInitPaymentMethod(true);
            } else if (createResult.new_amount) {
              customShowToast("價錢已更改，請重新確認", "error", () =>
                window.location.reload()
              );
            }

            setRetryData({
              monthly_order_id: createResult.monthly_order_id,
              payment_id: createResult.payment_id,
            });
            // setReInitPaymentMethod(true);
            setGlobalLoadingState(false);
          }
        }
      }
    } catch (error) {
      setGlobalLoadingState(false);
    } finally {
      setGlobalLoadingState(false);
    }
  };

  // by using credit card
  const createSubscriptionHandler = async (state: IPaymentMethodsInfo) => {
    try {
      creditCardCreateSubscription(state);
    } catch (error) {}
  };

  const customGoBack = () => {
    if (selectedMonthlyItem?.extra_car_plate_price_id !== 25) {
      goStep(4);
    } else {
      if (extraCarPlateDetails.length > 0) {
        goStep(6);
      } else {
        goStep(5);
      }
    }
  };

  useEffect(() => {
    if (readyToRedirect) {
      // redirect to payment result page
      navigate(`${ROUTE_PATHS.PAYMENT_RESULT_PAGE}?orderId=${readyToRedirect}`);
    }
  }, [readyToRedirect]);

  return (
    <div className="flex flex-1 flex-col gap-4" {...otherProps}>
      <BaseBreadCrumb onClick={customGoBack}>{`<上一頁`}</BaseBreadCrumb>
      <BaseTitle text="確認資料及付款" />

      {/* booking detail block */}
      {applyFormData && (
        <BookingDetailBlock
          name={applyFormData?.name}
          carPlate={applyFormData.mainCarPlate}
          phone={applyFormData?.phone}
          email={applyFormData?.email}
          parkingName={selectedCarparkInfo?.display_name!}
          parkingAddress={selectedCarparkInfo?.location!}
          startDate={dayjs(selectedStartDate).format("YYYY-MM-DD")}
          octopusNumber={
            applyFormData?.octopusNumber
              ? `${applyFormData?.octopusNumber}(${applyFormData?.octopusLastDigit})`
              : undefined
          }
          itemTypeID={selectedMonthlyItem?.item_type_id!}
          itemName={
            selectedMonthlyItem?.description
              ? `${selectedMonthlyItem?.display_name}(${selectedMonthlyItem?.description})`
              : selectedMonthlyItem?.display_name!
          }
          interval_count={selectedMonthlyItem?.interval_count || 1}
          price={selectedMonthlyItem?.price || 0}
          extra_car_plate_price={
            selectedMonthlyItem?.extra_car_plate_price || 0
          }
          extraCarPlateDetails={extraCarPlateDetails}
        />
      )}

      {/* tnc section */}
      <div className="text-[0.625rem] text-white font-medium">
        *月租車位計劃將自動續期。計劃可隨時取消。
        <a
          href={"/example.pdf"}
          target="_blank"
          className="link text-mainYellow"
          rel="noreferrer"
        >
          進一步了解
        </a>
      </div>

      {/* checkbox */}
      <div className="flex flex-row items-center text-[0.625rem] text-white font-medium gap-2">
        <input
          type="checkbox"
          className="checkbox w-4 h-4 checkbox-warning rounded-md"
          checked={tncChecked}
          onChange={() => setTncChecked((prev) => !prev)}
        />
        <span>
          我同意
          <a
            href="https://parkingbees.com.hk/assets/pdf/parkingbees-tnc.pdf"
            target="_blank"
            className="link text-mainYellow"
            rel="noreferrer"
          >
            條款及細則
          </a>
          &nbsp; 及 &nbsp;
          <a
            href="https://parkingbees.com.hk/assets/pdf/parkingbees-monthly-tnc.pdf"
            target="_blank"
            className="link text-mainYellow"
            rel="noreferrer"
          >
            月租車位使用條款
          </a>
        </span>
      </div>

      {/* payment method select section */}

      {tncChecked && (
        <SubscriptionPaymentMethodSelectionBlock
          adyenCheckout={(state) => createSubscriptionHandler(state!)}
          buttonDisabled={!tncChecked}
          reInitPaymentMethod={reInitPaymentMethod}
          paymentId={retryData?.payment_id?.toString() ?? undefined}
          createSubscriptionCallback={nonCreditCardCreateSubscription}
          returnUrl={`${window.location.origin}${ROUTE_PATHS.PAYMENT_RESULT_PAGE}?orderId=${retryData?.monthly_order_id}`}
        />
      )}
    </div>
  );
};

export default ConfirmForm;
